import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import Avatar from "@material-ui/core/Avatar";
import Dialog from "@material-ui/core/Dialog";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import { formatarTelefone, formatarNomeOuTelefone } from "../../utils/phone";
import { painelAstro } from "../../utils/estiloAstro";
import { APARELHOS, textoPresenca } from "../TicketInfo/infoCliente";
import { agruparFicha } from "../../utils/fichaCandidato";

const drawerWidth = 380;

// Ficha do candidato: os campos ficam em abas (Pessoais, Escolaridade...),
// conforme o grupo salvo no nome do campo ("Grupo|Campo").

// Abas da barra: Pessoais, Escolaridade e Experiência ficam juntas numa aba
// só ("Currículo"), uma seção embaixo da outra.
const ABAS_FICHA = [
	{ id: "curriculo", curto: "Currículo", grupos: ["Dados pessoais", "Escolaridade", "Experiência"] },
	{ id: "vaga", curto: "Vaga", grupos: ["Vaga e entrevista"] },
	{ id: "outras", curto: "Outras", grupos: ["Outras informações"] },
];

const TITULO_SECAO = {
	"Dados pessoais": "Dados pessoais",
	Escolaridade: "Escolaridade",
	"Experiência": "Experiência profissional",
};

// Campo longo ocupa a linha inteira; o resto fica em duas colunas.
const ehLongo = (info) =>
	/m[aã]e|endere|logradouro|ocupa|empresa|empregador|curso|institui|experi|cargo|observ|resumo|habilidade|nome completo/i.test(info.campo) ||
	String(info.campo || "").length > 22 ||
	String(info.value || "").length > 18;

const useStyles = makeStyles((theme) => {
	const escuro = theme.palette.type === "dark";
	const verde = escuro ? "#5eead4" : "#0D9488";
	return {
		drawer: {
			width: drawerWidth,
			flexShrink: 0,
			"@media (max-width: 600px)": { width: "100vw" },
		},
		drawerPaper: {
			width: drawerWidth,
			display: "flex",
			border: "none",
			borderLeft: `1px solid ${theme.palette.divider}`,
			"@media (max-width: 600px)": { width: "100vw" },
		},
		header: {
			display: "flex",
			gap: 6,
			borderBottom: `1px solid ${theme.palette.divider}`,
			backgroundColor: escuro ? "#162221" : "#ffffff",
			alignItems: "center",
			padding: theme.spacing(0, 1),
			minHeight: "72px",
			flexShrink: 0,
		},
		tituloHeader: {
			fontWeight: 700,
			fontSize: "0.98rem",
			color: theme.palette.text.primary,
		},
		content: {
			display: "flex",
			gap: 10,
			backgroundColor: escuro ? "#0f1727" : "#f2f8f7",
			flexDirection: "column",
			padding: 10,
			height: "100%",
			overflowY: "auto",
			...theme.scrollbarStyles,
			"& > *": { flexShrink: 0 },
		},

		// ---- cartao do topo ---------------------------------------------------
		cartao: {
			...painelAstro(theme),
			padding: 12,
			position: "relative",
		},
		topo: {
			display: "flex",
			gap: 12,
			alignItems: "center",
		},
		avatar: {
			width: 64,
			height: 64,
			flexShrink: 0,
			boxShadow: `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 5px #14b8a6`,
			background: "linear-gradient(135deg, #0d9488, #14b8a6)",
		},
		avatarClicavel: { cursor: "zoom-in" },
		identidade: { minWidth: 0, flex: 1 },
		nome: {
			fontWeight: 800,
			fontSize: "0.95rem",
			lineHeight: 1.25,
			color: theme.palette.text.primary,
			display: "-webkit-box",
			WebkitLineClamp: 2,
			WebkitBoxOrient: "vertical",
			overflow: "hidden",
			paddingRight: 28,
		},
		telefone: {
			display: "inline-block",
			marginTop: 4,
			fontWeight: 700,
			fontSize: "0.8rem",
			"& a": { color: escuro ? "#5eead4" : "#0f766e" },
		},
		presenca: {
			display: "flex",
			alignItems: "center",
			gap: 5,
			marginTop: 2,
			fontSize: "0.74rem",
			color: theme.palette.text.secondary,
		},
		bolinha: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
		editar: {
			position: "absolute",
			top: 6,
			right: 6,
			padding: 6,
			color: verde,
		},
		conexao: {
			display: "inline-flex",
			alignItems: "center",
			gap: 5,
			marginTop: 10,
			padding: "3px 9px",
			borderRadius: 8,
			fontSize: "0.72rem",
			fontWeight: 600,
			maxWidth: "100%",
			overflow: "hidden",
			whiteSpace: "nowrap",
			textOverflow: "ellipsis",
		},
		conexaoOk: { backgroundColor: "rgba(13,148,136,0.12)", color: "#0D9488" },
		conexaoCaiu: { backgroundColor: "rgba(245,158,11,0.16)", color: "#b45309", fontWeight: 700 },
		conexaoAviso: { backgroundColor: "#dc2626", color: "#fff", fontWeight: 700 },
		atribuido: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			marginTop: 8,
			fontSize: "0.78rem",
			color: theme.palette.text.secondary,
			minWidth: 0,
			"& b": { color: theme.palette.text.primary, fontWeight: 700 },
		},
		atribuidoTexto: {
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
			minWidth: 0,
		},
		recado: {
			marginTop: 10,
			padding: "6px 10px",
			borderRadius: 8,
			fontSize: "0.78rem",
			fontStyle: "italic",
			lineHeight: 1.35,
			color: theme.palette.text.secondary,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			wordBreak: "break-word",
		},

		// ---- resumo do WhatsApp ----------------------------------------------
		tituloSecao: {
			fontSize: "0.7rem",
			fontWeight: 700,
			letterSpacing: "0.06em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary,
			margin: "0 0 8px 2px",
		},
		resumo: {
			display: "grid",
			gridTemplateColumns: "1fr 1fr",
			gap: 6,
		},
		item: {
			padding: "7px 10px",
			borderRadius: 8,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			minWidth: 0,
		},
		itemRotulo: {
			fontSize: "0.62rem",
			fontWeight: 600,
			letterSpacing: "0.04em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary,
			marginBottom: 1,
		},
		itemValor: {
			fontSize: "0.82rem",
			fontWeight: 600,
			color: theme.palette.text.primary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		valorAzul: { color: "#1A73E8" },
		valorVermelho: { color: "#D93025" },
		itemAlerta: {
			gridColumn: "1 / -1",
			backgroundColor: "#D93025",
			borderColor: "#D93025",
			"& $itemRotulo, & $itemValor": { color: "#fff" },
		},

		// ---- abas + campos ----------------------------------------------------
		abas: {
			display: "flex",
			gap: 4,
			padding: 3,
			borderRadius: 10,
			marginBottom: 10,
			backgroundColor: escuro ? "rgba(255,255,255,0.05)" : "#e6f2f0",
		},
		aba: {
			flex: 1,
			border: "none",
			cursor: "pointer",
			padding: "6px 4px",
			borderRadius: 8,
			fontSize: "0.74rem",
			fontWeight: 700,
			fontFamily: "inherit",
			color: theme.palette.text.secondary,
			backgroundColor: "transparent",
			transition: "background-color .15s ease, color .15s ease",
			"&:hover": { color: theme.palette.text.primary },
		},
		abaAtiva: {
			color: "#fff",
			backgroundColor: "#0D9488",
			"&:hover": { color: "#fff" },
		},
		contador: { opacity: 0.75, fontWeight: 600, marginLeft: 3 },
		campos: {
			display: "grid",
			gridTemplateColumns: "1fr 1fr",
			gridAutoFlow: "row dense",
			gap: 6,
		},
		campo: {
			padding: "7px 10px",
			borderRadius: 8,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			minWidth: 0,
			cursor: "copy",
			transition: "background-color .15s ease",
			"&:hover": { backgroundColor: escuro ? "rgba(255,255,255,0.08)" : "#edf1ef" },
		},
		campoLongo: { gridColumn: "1 / -1" },
		secaoFicha: {
			"& + &": {
				marginTop: 12,
				paddingTop: 10,
				borderTop: `1px dashed ${theme.palette.divider}`,
			},
		},
		secaoFichaTitulo: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			margin: "0 2px 6px",
			fontSize: "0.72rem",
			fontWeight: 800,
			color: verde,
			"&::before": {
				content: '""',
				width: 3,
				height: 12,
				borderRadius: 2,
				backgroundColor: verde,
			},
		},
		campoValor: {
			fontSize: "0.84rem",
			fontWeight: 600,
			lineHeight: 1.35,
			color: theme.palette.text.primary,
			whiteSpace: "normal",
			wordBreak: "break-word",
		},
		copiado: { color: verde, fontWeight: 700 },
		vazio: {
			fontSize: "0.78rem",
			color: theme.palette.text.secondary,
			textAlign: "center",
			padding: "10px 0",
		},

		fotoAmpliada: {
			display: "block",
			maxWidth: "min(78vw, 520px)",
			maxHeight: "78vh",
			width: "auto",
			height: "auto",
			borderRadius: 12,
		},
	};
});

// ───────── Grupo: descrição + participantes (lista compacta com rolagem própria) ─────────
const VERDE_G = "#0d9488";
const estG = {
	bloco: {
		borderRadius: 14, border: "1px solid #e1eeec", background: "#fafcfb",
		padding: "12px 14px", marginBottom: 12, minWidth: 0,
	},
	rotulo: {
		fontSize: "0.7rem", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase",
		color: "#64748b", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
	},
	desc: {
		fontSize: "0.8rem", lineHeight: 1.45, color: "#334155", whiteSpace: "pre-wrap",
		overflowWrap: "anywhere", wordBreak: "break-word",
	},
	link: { background: "none", border: 0, padding: 0, marginTop: 6, color: VERDE_G, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit" },
	busca: {
		width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 10,
		border: "1px solid #d5e6e3", fontFamily: "inherit", fontSize: "0.82rem", outline: "none", background: "#fff",
	},
	lista: {
		maxHeight: 340, overflowY: "auto", overflowX: "hidden", marginTop: 8, marginRight: -6, paddingRight: 6,
		scrollbarWidth: "thin", scrollbarColor: "rgba(13,148,136,.35) transparent",
	},
	grupoTitulo: { fontSize: "0.68rem", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em", margin: "8px 2px 4px" },
	linha: { display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", borderRadius: 10, minWidth: 0 },
	avatar: { width: 30, height: 30, fontSize: 13, fontWeight: 700, background: "rgba(13,148,136,.12)", color: VERDE_G, flexShrink: 0 },
	nome: { fontWeight: 700, fontSize: "0.83rem", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
	tel: { fontSize: "0.72rem", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
	selo: { flexShrink: 0, fontSize: "0.62rem", fontWeight: 800, color: VERDE_G, background: "rgba(13,148,136,.12)", padding: "2px 7px", borderRadius: 999 },
	aviso: { fontSize: "0.72rem", color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 8px", marginBottom: 8 },
};

const iniciais = nome => {
	const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
	if (!partes.length) return "";
	return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
};

const LinhaParticipante = ({ p }) => {
	const titulo = p.nome || (p.numero ? formatarTelefone(p.numero) : "Participante");
	const history = useHistory();
	const [abrindo, setAbrindo] = useState(false);
	const podeConversar = !!(p.numero || p.contactId);
	const conversar = async () => {
		if (!podeConversar || abrindo) return;
		setAbrindo(true);
		try {
			const { data } = await api.post("/contacts/conversa-direta", {
				contactId: p.contactId,
				numero: p.numero,
				nome: p.nome,
			});
			toast.success("Conversa aberta (aba Internos).");
			history.push(`/atendimentos/${data.ticketId}`);
		} catch (err) {
			toastError(err);
		} finally {
			setAbrindo(false);
		}
	};
	return (
		<div style={estG.linha} title={p.nome && p.numero ? `${p.nome} · ${formatarTelefone(p.numero)}` : titulo}>
			<Avatar src={p.foto || undefined} style={estG.avatar}>
				{iniciais(p.nome) || <PersonOutlineIcon style={{ fontSize: 17 }} />}
			</Avatar>
			<div style={{ minWidth: 0, flex: 1 }}>
				<div style={estG.nome}>{titulo}</div>
				{p.nome && p.numero ? <div style={estG.tel}>{formatarTelefone(p.numero)}</div> : null}
			</div>
			{p.admin ? <span style={estG.selo}>Admin</span> : null}
			{!p.admin && p.mensagens ? <span style={{ ...estG.tel, flexShrink: 0 }}>{p.mensagens} msg</span> : null}
			{podeConversar ? (
				<button
					type="button"
					onClick={conversar}
					disabled={abrindo}
					title="Mandar mensagem só para esta pessoa"
					style={{
						flexShrink: 0, border: 0, background: "rgba(13,148,136,.1)", color: VERDE_G,
						width: 28, height: 28, borderRadius: 8, cursor: "pointer", display: "flex",
						alignItems: "center", justifyContent: "center", opacity: abrindo ? 0.5 : 1,
					}}
				>
					<ChatBubbleOutlineIcon style={{ fontSize: 15 }} />
				</button>
			) : null}
		</div>
	);
};

const GrupoParticipantes = ({ info, carregando, busca, setBusca, lista }) => {
	const [descAberta, setDescAberta] = useState(false);
	const todos = info?.participantes || [];
	const admins = lista.filter(p => p.admin);
	const membros = lista.filter(p => !p.admin);
	const descricao = String(info?.descricao || "").trim();
	const descLonga = descricao.length > 180 || descricao.split("\n").length > 4;

	return (
		<>
			{descricao ? (
				<div style={estG.bloco}>
					<div style={estG.rotulo}>Descrição do grupo</div>
					<div
						style={{
							...estG.desc,
							...(descLonga && !descAberta
								? { display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }
								: {}),
						}}
					>
						{descricao}
					</div>
					{descLonga ? (
						<button type="button" style={estG.link} onClick={() => setDescAberta(v => !v)}>
							{descAberta ? "Ver menos" : "Ver mais"}
						</button>
					) : null}
				</div>
			) : null}

			<div style={estG.bloco}>
				<div style={estG.rotulo}>
					<span>Participantes</span>
					{todos.length ? (
						<span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 700 }}>
							{todos.length} · {todos.filter(p => p.admin).length} admin
						</span>
					) : null}
				</div>

				{carregando && <div style={estG.tel}>Carregando participantes…</div>}

				{!carregando && info?.fonte === "historico" && (
					<div style={estG.aviso}>WhatsApp desconectado: mostrando só quem já mandou mensagem no grupo.</div>
				)}

				{!carregando && info && !todos.length && (
					<div style={estG.tel}>{info.erro ? "Não consegui carregar os participantes." : "Nenhum participante encontrado."}</div>
				)}

				{todos.length > 6 && (
					<input
						value={busca}
						onChange={e => setBusca(e.target.value)}
						placeholder="Buscar por nome ou número…"
						style={estG.busca}
					/>
				)}

				{todos.length ? (
					<div style={estG.lista}>
						{admins.length ? <div style={estG.grupoTitulo}>Administradores</div> : null}
						{admins.map((p, i) => <LinhaParticipante key={`a-${p.numero || p.contactId || i}-${i}`} p={p} />)}
						{membros.length ? <div style={estG.grupoTitulo}>Membros</div> : null}
						{membros.map((p, i) => <LinhaParticipante key={`m-${p.numero || p.contactId || i}-${i}`} p={p} />)}
						{!lista.length ? <div style={{ ...estG.tel, padding: 8 }}>Ninguém encontrado com “{busca}”.</div> : null}
					</div>
				) : null}
			</div>
		</>
	);
};

const ContactDrawer = ({
	open,
	handleDrawerClose,
	contact,
	ticketId,
	atendente,
	atribuidoPor,
	loading,
	conexaoNome,
	conexaoStatus,
	semConexao,
}) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [fotoAberta, setFotoAberta] = useState(false);
	const [info, setInfo] = useState(null);
	const [aba, setAba] = useState(ABAS_FICHA[0].id);
	const [copiado, setCopiado] = useState(null);

	// Grupo: quem participa (do WhatsApp; sem conexao, pelo historico).
	const ehGrupo = !!contact?.isGroup;
	const [grupoInfo, setGrupoInfo] = useState(null);
	const [carregandoGrupo, setCarregandoGrupo] = useState(false);
	const [buscaParticipante, setBuscaParticipante] = useState("");

	useEffect(() => {
		let vivo = true;
		setGrupoInfo(null);
		setBuscaParticipante("");
		if (!open || !ehGrupo || !contact?.id) return undefined;
		setCarregandoGrupo(true);
		api
			.get(`/contacts/${contact.id}/participantes`)
			.then(({ data }) => vivo && setGrupoInfo(data))
			.catch(() => vivo && setGrupoInfo({ erro: true, participantes: [] }))
			.finally(() => vivo && setCarregandoGrupo(false));
		return () => {
			vivo = false;
		};
	}, [open, ehGrupo, contact?.id]);

	const participantesFiltrados = (grupoInfo?.participantes || []).filter(p => {
		const t = buscaParticipante.trim().toLowerCase();
		if (!t) return true;
		return (p.nome || "").toLowerCase().includes(t) || (p.numero || "").includes(t.replace(/\D/g, "") || "§");
	});

	// Mesmas informacoes do cabecalho, buscadas quando a barra abre.
	useEffect(() => {
		if (!open || !ticketId) return undefined;
		let ativo = true;
		api
			.get(`/tickets/${ticketId}/info-cliente`)
			.then(({ data }) => ativo && setInfo(data))
			.catch(() => {});
		return () => {
			ativo = false;
		};
	}, [open, ticketId]);

	useEffect(() => {
		setInfo(null);
	}, [ticketId]);

	const porAba = useMemo(() => agruparFicha(contact?.extraInfo), [contact]);

	const qtdDaAba = (a) => a.grupos.reduce((t, g) => t + (porAba[g] || []).length, 0);
	// Abas que tem algum dado; se a escolhida estiver vazia, mostra a primeira com dado.
	const abasComDados = ABAS_FICHA.filter((a) => qtdDaAba(a));
	const abaAtual =
		abasComDados.find((a) => a.id === aba) || abasComDados[0] || ABAS_FICHA[0];
	const abaVisivel = abaAtual.id;
	const secoesVisiveis = abaAtual.grupos.filter((g) => (porAba[g] || []).length);

	const copiar = (valor, chave) => {
		try {
			navigator.clipboard?.writeText(String(valor));
			setCopiado(chave);
			setTimeout(() => setCopiado((c) => (c === chave ? null : c)), 1200);
		} catch {
			/* sem clipboard */
		}
	};

	const pres = textoPresenca(
		info?.presenca && Date.now() - info.presenca.em < 30 * 60 * 1000 ? info.presenca : null,
		Date.now()
	);

	const resumo = [];
	if (info) {
		if (APARELHOS[info.aparelho]) {
			resumo.push({
				rotulo: "Aparelho",
				valor: APARELHOS[info.aparelho].rotulo,
				classe: info.aparelho === "iphone" ? classes.valorAzul : classes.valorVermelho,
			});
		}
		if (info.uf) resumo.push({ rotulo: "Região", valor: `${info.uf} · DDD ${info.ddd}` });
		resumo.push({ rotulo: "Conta", valor: info.business ? "WhatsApp Business" : "Pessoal" });
		resumo.push({
			rotulo: "Atendimentos",
			valor: info.atendimentos > 1 ? `${info.atendimentos}º atendimento` : "Primeiro",
		});
		if (info.vistoDesligado) resumo.push({ rotulo: "Confirmação de leitura", valor: "Desligada" });
	}

	const classeConexao = semConexao
		? classes.conexaoAviso
		: conexaoStatus && conexaoStatus !== "CONNECTED"
		? classes.conexaoCaiu
		: classes.conexaoOk;

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{ paper: classes.drawerPaper }}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography className={classes.tituloHeader}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					{/* ---- quem e o cliente ---- */}
					<Paper square variant="outlined" className={classes.cartao}>
						<Tooltip title={i18n.t("contactDrawer.buttons.edit")}>
							<IconButton className={classes.editar} onClick={() => setModalOpen(true)}>
								<EditOutlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<div className={classes.topo}>
							<Avatar
								alt={contact.name}
								src={contact.profilePicUrl}
								className={`${classes.avatar} ${contact.profilePicUrl ? classes.avatarClicavel : ""}`}
								onClick={() => contact.profilePicUrl && setFotoAberta(true)}
								title={contact.profilePicUrl ? "Ver foto ampliada" : ""}
							/>
							<div className={classes.identidade}>
								<div className={classes.nome}>
									{formatarNomeOuTelefone(contact?.name, contact?.number)}
								</div>
								<div className={classes.telefone}>
									<Link href={`tel:${contact.number}`}>{formatarTelefone(contact.number)}</Link>
								</div>
								{pres && (
									<div className={classes.presenca}>
										<span
											className={classes.bolinha}
											style={{ backgroundColor: pres.tipo === "offline" ? "#b8c0c8" : "#0D9488" }}
										/>
										<span style={pres.tipo !== "offline" ? { color: "#0D9488", fontWeight: 600 } : undefined}>
											{pres.texto}
										</span>
									</div>
								)}
							</div>
						</div>

						{/* Por qual numero nosso falamos com este cliente. */}
						{(semConexao || conexaoNome) && (
							<div
								className={`${classes.conexao} ${classeConexao}`}
								title={semConexao ? "Use “Trocar conexão” no menu do atendimento" : conexaoNome}
							>
								{semConexao ? (
									<SignalWifiOffIcon style={{ fontSize: 15 }} />
								) : (
									<SettingsInputAntennaIcon style={{ fontSize: 15 }} />
								)}
								<span>
									{semConexao
										? `Sem conexão${conexaoNome ? ` — era “${conexaoNome}”` : ""}`
										: `${conexaoNome}${conexaoStatus && conexaoStatus !== "CONNECTED" ? " — desconectado" : ""}`}
								</span>
							</div>
						)}

						{/* Quem esta com o cliente agora (e quem atribuiu). */}
						<div className={classes.atribuido}>
							<PersonOutlineIcon style={{ fontSize: 16, flexShrink: 0 }} />
							<span
								className={classes.atribuidoTexto}
								title={atendente ? `Atribuído à ${atendente}${atribuidoPor ? ` · por ${atribuidoPor}` : ""}` : undefined}
							>
								{atendente ? (
									<>
										Atribuído à <b>{atendente}</b>
										{atribuidoPor ? ` · por ${atribuidoPor}` : ""}
									</>
								) : (
									"Sem atendente (com a I.A ou na fila)"
								)}
							</span>
						</div>

						{info?.recado && (
							<div className={classes.recado} title="Recado do perfil do WhatsApp">
								“{info.recado}”
							</div>
						)}
					</Paper>

					{/* ---- resumo do WhatsApp ---- */}
					{info && (
						<Paper square variant="outlined" className={classes.cartao}>
							<Typography className={classes.tituloSecao}>WhatsApp</Typography>
							<div className={classes.resumo}>
								{info.naoEstaRecebendo && (
									<div className={`${classes.item} ${classes.itemAlerta}`}>
										<div className={classes.itemRotulo}>Entrega</div>
										<div className={classes.itemValor} style={{ whiteSpace: "normal" }}>
											Não está recebendo as mensagens
										</div>
										<div className={classes.itemRotulo} style={{ marginTop: 4, textTransform: "none", letterSpacing: 0 }}>
											Tente ligar para o candidato.
										</div>
									</div>
								)}
								{resumo.map((r) => (
									<div key={r.rotulo} className={classes.item} title={r.dica || r.valor}>
										<div className={classes.itemRotulo}>{r.rotulo}</div>
										<div className={`${classes.itemValor} ${r.classe || ""}`}>{r.valor}</div>
									</div>
								))}
							</div>
						</Paper>
					)}

					{/* ---- grupo: participantes ---- */}
					{ehGrupo && (
						<Paper square variant="outlined" className={classes.cartao}>
							<GrupoParticipantes
								info={grupoInfo}
								carregando={carregandoGrupo}
								busca={buscaParticipante}
								setBusca={setBuscaParticipante}
								lista={participantesFiltrados}
							/>
						</Paper>
					)}

					{/* ---- ficha do candidato, em abas ---- */}
					{!ehGrupo && (
						<Paper square variant="outlined" className={classes.cartao}>
							<Typography className={classes.tituloSecao}>Ficha do candidato</Typography>
							{abasComDados.length > 0 && (
								<div className={classes.abas}>
									{abasComDados.map((a) => (
										<button
											key={a.id}
											type="button"
											title={a.grupos.join(" · ")}
											className={`${classes.aba} ${abaVisivel === a.id ? classes.abaAtiva : ""}`}
											onClick={() => setAba(a.id)}
										>
											{a.curto}
											<span className={classes.contador}>{qtdDaAba(a)}</span>
										</button>
									))}
								</div>
							)}

							{secoesVisiveis.length ? (
								secoesVisiveis.map((grupo) => (
									<div key={grupo} className={classes.secaoFicha}>
										{abaAtual.grupos.length > 1 && (
											<div className={classes.secaoFichaTitulo}>{TITULO_SECAO[grupo] || grupo}</div>
										)}
										<div className={classes.campos}>
											{porAba[grupo].map((c, i) => {
												const chave = `${grupo}-${c.id || c.name}-${i}`;
												return (
													<div
														key={chave}
														className={`${classes.campo} ${ehLongo(c) ? classes.campoLongo : ""}`}
														onClick={() => copiar(c.value, chave)}
														title={`${c.value}\n(clique para copiar)`}
													>
														<div className={classes.itemRotulo}>
															{c.campo}
															{copiado === chave && <span className={classes.copiado}> · copiado</span>}
														</div>
														<div className={classes.campoValor}>{c.value}</div>
													</div>
												);
											})}
										</div>
									</div>
								))
							) : (
								<div className={classes.vazio}>
									Ainda sem dados. A assistente preenche quando o candidato manda o currículo ou conta as informações.
								</div>
							)}
						</Paper>
					)}

					<ContactModal
						open={modalOpen}
						onClose={() => setModalOpen(false)}
						contactId={contact.id}
						initialValues={contact}
					/>
				</div>
			)}

			<Dialog
				open={fotoAberta}
				onClose={() => setFotoAberta(false)}
				maxWidth={false}
				PaperProps={{ style: { backgroundColor: "transparent", boxShadow: "none", overflow: "visible" } }}
			>
				<img
					src={contact.profilePicUrl}
					alt={contact.name}
					className={classes.fotoAmpliada}
					onClick={() => setFotoAberta(false)}
					style={{ cursor: "zoom-out" }}
				/>
			</Dialog>
		</Drawer>
	);
};

export default ContactDrawer;
